import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Icon, Can, Button, Spinner, colors } from '@iq/react-components';
import { useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';

import StyledItem from '../../../../StyledItem';
import {
  parseSwitchgearCounterConfigTemplate,
  generateSwitchgearCounterConfigTemplate,
} from './utils';
import { displayNotification, getNotification } from '../../../../../bundles/notifications';
import { uploadSwitchgearMonitoringConfig } from '../../../../../bundles/sources';

const ALLOWED_FILE_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
]);
const ALLOWED_EXTENSIONS = new Set(['.xlsx', '.xls']);

const getSourcesEnabledState = (sources) => {
  const initialSourcesEnabled = {};

  for (const source of sources) {
    initialSourcesEnabled[source.id] = source.options.analytics?.swgCounter?.enabled || false;
  }

  return initialSourcesEnabled;
};

const SwitchgearCounterItem = ({ site, source, sourcesEnabled, setSourcesEnabled, hasError }) => {
  const [isImportingConfig, setIsImportingConfig] = useState(false);
  const configInputReference = useRef(null);
  const dispatch = useDispatch();

  const { importedFileName, enabled: switchgearEnabled } =
    source.options.analytics?.swgCounter || {};
  const { dataSyncEnabled } = source.options;

  const onConfigFileChange = async (event) => {
    const [file] = event.target.files;
    if (!file) return;

    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    const isValidType = ALLOWED_FILE_TYPES.has(file.type);
    const isValidExtension = ALLOWED_EXTENSIONS.has(fileExtension);

    if (!isValidType && !isValidExtension) {
      dispatch(
        displayNotification(
          getNotification(
            'uploadSwitchgearMonitoringConfig',
            'error'
          )(['Invalid file type. Only Excel files (.xlsx, .xls) are allowed.'])
        )
      );
      return;
    }

    setIsImportingConfig(true);
    const { fileName, parsedConfig, errors } = await parseSwitchgearCounterConfigTemplate(file);
    if (errors.length > 0) {
      dispatch(
        displayNotification(getNotification('uploadSwitchgearMonitoringConfig', 'error')(errors))
      );
    } else {
      dispatch(uploadSwitchgearMonitoringConfig(site.id, source.id, parsedConfig, fileName));
    }
    setIsImportingConfig(false);
  };

  const openConfigFileInput = () => {
    configInputReference.current.value = '';
    configInputReference.current.click();
  };

  const toggleEnable = (event) => {
    setSourcesEnabled({ ...sourcesEnabled, [source.id]: event.target.checked });
  };

  return (
    <div
      className="switchgear-counter-source"
      key={source.id}
    >
      <div className="source-header">
        <div className="source-name">{source.name}</div>
        <Can
          permission="sources/Write"
          scope={{ org: site.org, site: site.id }}
        >
          <input
            type="file"
            accept=".xlsx"
            style={{ display: 'none' }}
            ref={configInputReference}
            onChange={(event) => onConfigFileChange(event)}
          />
          <Button
            className="switchgear-upload-button"
            activity="secondary"
            onClick={openConfigFileInput}
            disabled={isImportingConfig}
            tooltip="Upload switchgear monitoring configuration file"
          >
            {isImportingConfig ? (
              <Spinner />
            ) : (
              <>
                <Icon
                  size="l"
                  icon="he-upload"
                />
                Upload SW monitoring config
              </>
            )}
          </Button>
        </Can>
      </div>
      <div className="source-body">
        <div>
          <div className="switchgear-counter-enable">
            <input
              checked={sourcesEnabled[source.id]}
              id={`enable-switchgear-${source.id}`}
              type="checkbox"
              onChange={toggleEnable}
            />
            <label htmlFor={`enable-switchgear-${source.id}`}>Enable switchgear counter</label>
          </div>
          {switchgearEnabled && dataSyncEnabled === false && (
            <div className="switchgear-counter-datasync-warning">
              <span className="switchgear-counter-error">*</span> Data sync is disabled for this
              source
            </div>
          )}
        </div>
        <div className="switchgear-counter-file-info">
          {importedFileName && (
            <div className="switchgear-counter-file-name">
              <Icon
                icon="he-systemfeedbacksuccess"
                size="s"
                style={{ color: colors.StatusGreen }}
              />
              {importedFileName}
            </div>
          )}
          {hasError && !importedFileName && (
            <div className="switchgear-counter-error">No configuration file uploaded</div>
          )}
        </div>
      </div>
    </div>
  );
};

const SwitchgearCounter = ({ site = {}, sources, onUpdate, isOnPremise = false }) => {
  const [isOpen, setOpen] = useState(sources.length === 0);
  const [sourcesEnabled, setSourcesEnabled] = useState(() => getSourcesEnabledState(sources));
  const [errorSourceIds, setErrorSourceIds] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    setSourcesEnabled(getSourcesEnabledState(sources));
  }, [sources]);

  const updateSources = useCallback(() => {
    const sourcesWithErrors = [];

    for (const sourceId of Object.keys(sourcesEnabled)) {
      const source = sources.find((s) => s.id === sourceId);
      if (!source) continue;

      const hasConfigFile = source.options.analytics?.swgCounter?.importedFileName;
      if (sourcesEnabled[sourceId] && !hasConfigFile) {
        sourcesWithErrors.push(sourceId);
        continue;
      }

      if ((source.options.analytics?.swgCounter?.enabled || false) === sourcesEnabled[sourceId]) {
        continue;
      }

      const updatedSource = {
        ...source,
        options: {
          ...source.options,
          analytics: {
            ...source.options.analytics,
            swgCounter: {
              ...source.options?.analytics?.swgCounter,
              enabled: sourcesEnabled[sourceId],
            },
          },
        },
      };

      if (updatedSource.options.clientSecret !== undefined) {
        const sanitizedOptions = { ...updatedSource.options };
        delete sanitizedOptions.clientSecret;
        updatedSource.options = sanitizedOptions;
      }

      onUpdate(sourceId, updatedSource);
    }

    if (sourcesWithErrors.length > 0) {
      setErrorSourceIds(sourcesWithErrors);
      dispatch(
        displayNotification(
          getNotification(
            'uploadSwitchgearMonitoringConfig',
            'error'
          )(['Cannot enable switchgear counter without uploading a configuration file first.'])
        )
      );
    } else {
      setErrorSourceIds([]);
    }
  }, [sourcesEnabled, sources, onUpdate, dispatch]);

  return (
    <StyledItem
      itemClass="switchgear-counter"
      active={isOpen}
      onClick={() => sources.length > 0 && setOpen(!isOpen)}
      headerContent={
        <div className="title">
          Switchgear counter settings
          <Button
            design="text"
            slim
            icon={
              <Icon
                icon="he-info"
                size="s"
              />
            }
            tooltip="Use this setting to upload a configuration file and choose whether the system should process switchgear counter data based on it."
            onClick={(event) => event.preventDefault()}
          ></Button>
        </div>
      }
      headerActions={
        <>
          <Button
            className="template-download"
            design="text"
            activity="secondary"
            onClick={(event) => {
              event.stopPropagation();
              generateSwitchgearCounterConfigTemplate();
            }}
          >
            <Icon icon="he-download" />
            Download switchgear counter config template
          </Button>
          {sources.length > 0 ? <Icon icon={isOpen ? 'he-up' : 'he-down'} /> : null}
        </>
      }
      bodyActionsPosition="center"
    >
      {sources.map((source) => (
        <SwitchgearCounterItem
          site={site}
          sourcesEnabled={sourcesEnabled}
          setSourcesEnabled={setSourcesEnabled}
          source={source}
          key={source.id}
          hasError={errorSourceIds.includes(source.id)}
        />
      ))}
      {sources.length === 0 && !isOnPremise && (
        <div className="no-sources-div">
          There are currently no EC sources configured. Please{' '}
          <Can
            permission="sources/Write"
            scope={{ org: site.org, site: site.id }}
          >
            <NavLink
              exact
              to={{
                pathname: `/sites/${site.id}/admin/sources/`,
                search: globalThis.location.search,
              }}
            >
              click here
            </NavLink>
          </Can>{' '}
          to configure them first.
        </div>
      )}
      {sources.length > 0 ? (
        <div className="switchgear-counter-footer">
          {sources.length > 0 ? <Button onClick={updateSources}>Save</Button> : null}
        </div>
      ) : null}
    </StyledItem>
  );
};

export default SwitchgearCounter;
